/**
 * Role Constants and Utilities
 *
 * Access model:
 * 1) Company permission (allowPlanning, allowQC, etc.) — required for everyone, including superadmin.
 * 2) Role within module — superadmin gets all roles for modules the company subscribes to.
 */

import { hasCompanyPermissionFlag } from './companyPermissionFlags';

export const ROLE_NUMBERS = {
  ADMIN: 1,
  OPERATOR: 2,
  PLANNER: 3,
  QC_USER: 4,
  QC_ADMIN: 5,
  PLANT_COORDINATOR: 6,
  RECEIVING_USER: 7,
  FG_USER: 8,
  ENGINEERING: 9,
  ACCOUNTANT: 10,
  TOOLING: 11,
  BUSINESS_UNIT_USER: 12,
  SHIPPING_USER: 13,
  BUSINESS_UNIT_MANAGER: 14,
  PURCHASING_USER: 15,
  PURCHASING_MANAGER: 16,
  TOOLING_MANAGER: 17,
};

export const ROLE_NAMES = {
  [ROLE_NUMBERS.ADMIN]: 'Admin',
  [ROLE_NUMBERS.OPERATOR]: 'Operator',
  [ROLE_NUMBERS.PLANNER]: 'Planner',
  [ROLE_NUMBERS.QC_USER]: 'QC User',
  [ROLE_NUMBERS.QC_ADMIN]: 'QC Admin',
  [ROLE_NUMBERS.PLANT_COORDINATOR]: 'Plant Coordinator',
  [ROLE_NUMBERS.RECEIVING_USER]: 'Receiving User',
  [ROLE_NUMBERS.FG_USER]: 'FG User',
  [ROLE_NUMBERS.ENGINEERING]: 'Engineering',
  [ROLE_NUMBERS.ACCOUNTANT]: 'Accountant',
  [ROLE_NUMBERS.TOOLING]: 'Tooling',
  [ROLE_NUMBERS.BUSINESS_UNIT_USER]: 'Business Unit User',
  [ROLE_NUMBERS.SHIPPING_USER]: 'Shipping User',
  [ROLE_NUMBERS.BUSINESS_UNIT_MANAGER]: 'Business Unit Manager',
  [ROLE_NUMBERS.PURCHASING_USER]: 'Purchasing User',
  [ROLE_NUMBERS.PURCHASING_MANAGER]: 'Purchasing Manager',
  [ROLE_NUMBERS.TOOLING_MANAGER]: 'Tooling Manager',
};

export const isSuperAdmin = (user) => {
  if (!user) return false;
  return user.is_superadmin === true || user.is_superadmin === 1;
};

export function userRoleNumber(user) {
  if (!user || user.role == null) return null;
  const r = user.role;
  return typeof r === 'string' ? parseInt(r, 10) : r;
}

/** Exact role match only (no superadmin implied). */
export function hasRoleNumber(user, roleNumber) {
  if (!user) return false;
  return userRoleNumber(user) === roleNumber;
}

/** Legacy helpers — include superadmin for system-wide UI (not module gates). */
export const isAdmin = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.ADMIN) || isSuperAdmin(user);
};

export const isPlanner = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.PLANNER) || isSuperAdmin(user);
};

export const isQCAdmin = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.QC_ADMIN) || isSuperAdmin(user);
};

export const isQCUser = (user) => {
  if (!user) return false;
  return (
    hasRoleNumber(user, ROLE_NUMBERS.QC_USER) ||
    hasRoleNumber(user, ROLE_NUMBERS.QC_ADMIN) ||
    isSuperAdmin(user)
  );
};

export const isOperator = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.OPERATOR) || isSuperAdmin(user);
};

export const isPlantCoordinator = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.PLANT_COORDINATOR) || isSuperAdmin(user);
};

export const isReceivingUser = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.RECEIVING_USER) || isSuperAdmin(user);
};

export const isFGUser = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.FG_USER) || isSuperAdmin(user);
};

export const isEngineering = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.ENGINEERING) || isSuperAdmin(user);
};

export const isAccountant = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.ACCOUNTANT) || isSuperAdmin(user);
};

export const isTooling = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.TOOLING) || isSuperAdmin(user);
};

export const isToolingManager = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.TOOLING_MANAGER) || isSuperAdmin(user);
};

export const isBusinessUnitUser = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.BUSINESS_UNIT_USER) || isSuperAdmin(user);
};

export const isShippingUser = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.SHIPPING_USER) || isSuperAdmin(user);
};

export const isBusinessUnitManager = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.BUSINESS_UNIT_MANAGER) || isSuperAdmin(user);
};

export const isPurchasingUser = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.PURCHASING_USER) || isSuperAdmin(user);
};

export const isPurchasingManager = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.PURCHASING_MANAGER) || isSuperAdmin(user);
};

/** Company subscribed to a module (superadmin must have flag too). */
export const hasPlanningModuleAccess = (user) =>
  !!user && hasCompanyPermissionFlag(user, 'allowPlanning');

export const hasQCModuleAccess = (user) =>
  !!user && hasCompanyPermissionFlag(user, 'allowQC');

export const hasInventoryModuleAccess = (user) =>
  !!user && hasCompanyPermissionFlag(user, 'allowInventory');

export const hasEngineeringModuleAccess = (user) =>
  !!user && hasCompanyPermissionFlag(user, 'allowEngineering');

export const hasToolingModuleAccess = (user) =>
  !!user && hasCompanyPermissionFlag(user, 'allowTooling');

export const hasAccountingModuleAccess = (user) =>
  !!user && hasCompanyPermissionFlag(user, 'allowAccounting');

/** All roles within module when company has permission. */
export const canAccessPlanningModule = (user) => {
  if (!hasPlanningModuleAccess(user)) return false;
  if (isSuperAdmin(user)) return true;
  return (
    hasRoleNumber(user, ROLE_NUMBERS.PLANNER) ||
    hasRoleNumber(user, ROLE_NUMBERS.OPERATOR) ||
    hasRoleNumber(user, ROLE_NUMBERS.ADMIN)
  );
};

export const canAccessQCModule = (user) => {
  if (!hasQCModuleAccess(user)) return false;
  if (isSuperAdmin(user)) return true;
  return (
    hasRoleNumber(user, ROLE_NUMBERS.QC_ADMIN) ||
    hasRoleNumber(user, ROLE_NUMBERS.QC_USER) ||
    hasRoleNumber(user, ROLE_NUMBERS.ADMIN)
  );
};

export const canAccessEngineeringModule = (user) => {
  if (!hasEngineeringModuleAccess(user)) return false;
  if (isSuperAdmin(user)) return true;
  return (
    hasRoleNumber(user, ROLE_NUMBERS.ENGINEERING) ||
    hasRoleNumber(user, ROLE_NUMBERS.OPERATOR) ||
    hasRoleNumber(user, ROLE_NUMBERS.ADMIN)
  );
};

export const canAccessToolingModule = (user) => {
  if (!hasToolingModuleAccess(user)) return false;
  if (isSuperAdmin(user)) return true;
  return (
    hasRoleNumber(user, ROLE_NUMBERS.TOOLING) ||
    hasRoleNumber(user, ROLE_NUMBERS.TOOLING_MANAGER) ||
    hasRoleNumber(user, ROLE_NUMBERS.OPERATOR) ||
    hasRoleNumber(user, ROLE_NUMBERS.ADMIN)
  );
}


export const canAccessToolingManagement = (user) => {
  if (!hasToolingModuleAccess(user)) return false;
  if (isSuperAdmin(user)) return true;
  return (
    hasRoleNumber(user, ROLE_NUMBERS.TOOLING) ||
    hasRoleNumber(user, ROLE_NUMBERS.TOOLING_MANAGER) ||
    hasRoleNumber(user, ROLE_NUMBERS.OPERATOR) ||
    hasRoleNumber(user, ROLE_NUMBERS.ADMIN)
  );
};

/** Any role with access to Sales module sidebar */
export const canAccessSalesModule = (user) => {
  if (!user) return false;
  if (isSuperAdmin(user)) return true;
  return (
    hasRoleNumber(user, ROLE_NUMBERS.ADMIN) ||
    hasRoleNumber(user, ROLE_NUMBERS.ACCOUNTANT) ||
    hasRoleNumber(user, ROLE_NUMBERS.BUSINESS_UNIT_USER) ||
    hasRoleNumber(user, ROLE_NUMBERS.SHIPPING_USER) ||
    hasRoleNumber(user, ROLE_NUMBERS.BUSINESS_UNIT_MANAGER)
  );
};

/** Any role with access to Purchase module sidebar */
export const canAccessPurchaseModule = (user) => {
  if (!user) return false;
  if (isSuperAdmin(user)) return true;
  return (
    hasRoleNumber(user, ROLE_NUMBERS.ADMIN) ||
    hasRoleNumber(user, ROLE_NUMBERS.ACCOUNTANT) ||
    hasRoleNumber(user, ROLE_NUMBERS.PURCHASING_USER) ||
    hasRoleNumber(user, ROLE_NUMBERS.PURCHASING_MANAGER)
  );
};

const INVENTORY_RECEIVING_ITEMS = new Set([
  'master-raw-material',
  'master-location',
  'activate-storage',
  'receiving-summary',
  'rm-transfer-summary',
  'scrapping-summary',
  'inventory-status-overview',
  'reports-all',
]);

const INVENTORY_FG_ITEMS = new Set([
  'master-finished-goods',
  'master-location',
  'activate-storage',
  'fg-transfer-summary',
  'fg-delivery-summary',
  'scrapping-summary',
  'inventory-status-overview',
  'reports-all',
]);

export const canAccessInventoryPortalItem = (user, itemKey) => {
  if (!hasInventoryModuleAccess(user)) return false;
  if (isSuperAdmin(user)) return true;
  if (
    hasRoleNumber(user, ROLE_NUMBERS.PLANT_COORDINATOR) ||
    hasRoleNumber(user, ROLE_NUMBERS.ADMIN) ||
    hasRoleNumber(user, ROLE_NUMBERS.ACCOUNTANT)
  ) {
    return true;
  }
  if (hasRoleNumber(user, ROLE_NUMBERS.RECEIVING_USER)) {
    return INVENTORY_RECEIVING_ITEMS.has(itemKey);
  }
  if (hasRoleNumber(user, ROLE_NUMBERS.FG_USER)) {
    return INVENTORY_FG_ITEMS.has(itemKey);
  }
  if (hasRoleNumber(user, ROLE_NUMBERS.OPERATOR)) {
    return INVENTORY_RECEIVING_ITEMS.has(itemKey) || INVENTORY_FG_ITEMS.has(itemKey);
  }
  return false;
};

export const canAccessAccounting = (user) => {
  if (!hasAccountingModuleAccess(user)) return false;
  if (isSuperAdmin(user)) return true;
  return (
    hasRoleNumber(user, ROLE_NUMBERS.ADMIN) ||
    hasRoleNumber(user, ROLE_NUMBERS.QC_ADMIN) ||
    hasRoleNumber(user, ROLE_NUMBERS.ACCOUNTANT)
  );
};

export function canAccessModuleAccessibility(user) {
  return !!user && String(user.id) === '1' && user.username === 'test';
}

export const getRoleName = (roleNumber) => ROLE_NAMES[roleNumber] || 'Unknown';

export const hasRole = (user, roleNumber) => {
  if (!user) return false;
  return hasRoleNumber(user, roleNumber) || isSuperAdmin(user);
};
